import { forwardRef } from 'react';

import { updateLegacyUrls } from '@core/Utils/Strings/UpdateLegacyUrls';
import { Accordion } from '@gds/Accordion/Accordion';
import { Typography } from '@gds/Typography/Typography';
import { RichText } from '@page-builder/Components/RichText/RichText';
import { KenticoImage } from '@shared-components/KenticoImage/KenticoImage';

import { IMAGE_SIZES } from './ReviewSection.constant';
import { ReviewSectionProps } from './ReviewSection.entity';

import styles from './ReviewSection.module.css';

const ReviewSection = forwardRef<HTMLElement, ReviewSectionProps>(
  ({ id, sectionData }: ReviewSectionProps, ref) => {
    return (
      <section className={styles.wrapper} id={id} ref={ref}>
        {sectionData.map(item => {
          const { section_header, summary, image, body_content, section_content } = item.elements;
          const isMainSection = !!body_content;
          const firstImg = image?.value?.[0];

          // There should be one main section followed by multiple FAQ sections, but this cant be guaranteed
          if (isMainSection) {
            return (
              <div key={section_header.value} className={styles.mainSection}>
                <header>
                  <Typography variant="h2">{section_header.value}</Typography>
                  {summary && (
                    <Typography className={styles.summary} variant="body3">
                      {summary.value}
                    </Typography>
                  )}
                </header>
                {firstImg && (
                  <div className={styles.img}>
                    <KenticoImage
                      src={firstImg.url}
                      alt={firstImg.description || ''}
                      sizes={IMAGE_SIZES}
                      fill
                    />
                  </div>
                )}
                {body_content && (
                  <RichText
                    className={styles.body}
                    noPadding
                    richText={updateLegacyUrls(body_content.value)}
                  />
                )}
              </div>
            );
          }

          if (!isMainSection && section_content) {
            return (
              <Accordion
                title={
                  <Typography className={styles.accordionTitle} Component="h2" variant="h4">
                    {section_header.value}
                  </Typography>
                }
                key={section_header.value}
                open={false}
              >
                <RichText
                  className={styles.body}
                  noPadding
                  richText={updateLegacyUrls(section_content.value)}
                />
              </Accordion>
            );
          }
        })}
      </section>
    );
  },
);

ReviewSection.displayName = 'ReviewSection';

export default ReviewSection;
